export type AppPermission = "admin" | "create" | "download" | "view"

export const SESSION_IDLE_TIMEOUT_MS = 15 * 60 * 1000
export const AUTH_CLEARED_EVENT = "vpn-auth-cleared"

export type CurrentUser = {
  username: string
  fullName: string
  email: string
  permissions: AppPermission[]
}

export type LoginResponse = {
  currentUser: CurrentUser
}

export type StatusResponse = {
  setupRequired: boolean
  currentUser: CurrentUser | null
  machines: string[]
  sshCertificateReady: boolean
}

export type VpnClient = {
  name: string
  fullName: string
  email: string
  status: "active" | "inactive"
  createdAt: string
  lastConnectedAt: string
  revokedAt: string
  createdByAppUser: string
  revokedByAppUser: string
}

export type VpnClientEvent = {
  eventType:
    | "created"
    | "revoked"
    | "reissued"
    | "downloaded"
    | "emailed"
    | "updated"
  eventAt: string
  appUsername: string
  note: string
}

export type VpnConnectionSession = {
  connectedSince: string
  realAddress: string
  vpnIp: string
  firstSeenAt: string
  lastSeenAt: string
  endedAt: string | null
  active: boolean
}

export type ConnectedClient = {
  name: string
  real_address: string
  vpn_ip: string
  bytes_received: number
  bytes_sent: number
  connected_since: string
}

export type AppUser = {
  username: string
  fullName: string
  email: string
  isActive: boolean
  permissions: AppPermission[]
  createdAt: string
}

export type Certificate = {
  id: number
  label: string
  key_path: string
  imported_at: string
  /** `stored` = PEM kept in SQLite; `file` = path on the server disk. */
  key_storage: "file" | "stored"
}

export type Destination = {
  machine_name: string
  is_active: boolean
  ssh_host: string
  ssh_user: string
  openvpn_mode: string
  openvpn_script_path: string
  remote_file_template: string
  certificate_id: number | null
  ssh_key_path: string
}

export type MailSettings = {
  resend_api_key_configured: boolean
  resend_api_key_masked: string
  from_name: string
  from_email: string
}

function emitAuthCleared(): void {
  try {
    window.dispatchEvent(new Event(AUTH_CLEARED_EVENT))
  } catch {
    /* ignore */
  }
}

function forgetLegacyToken(): void {
  try {
    localStorage.removeItem("vpn_app_token")
  } catch {
    /* ignore */
  }
}

forgetLegacyToken()

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string> | undefined),
    },
  })
  const data = (await res.json()) as { error?: string }
  if (!res.ok) {
    if (res.status === 401 && path !== "/api/login") emitAuthCleared()
    throw new Error(data.error || "Erro desconhecido.")
  }
  return data as T
}

export const api = {
  getStatus: () => request<StatusResponse>("/api/status"),

  login: async (username: string, password: string) => {
    return request<LoginResponse>("/api/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    })
  },

  logout: async () => {
    try {
      await request<object>("/api/logout", { method: "POST" })
    } catch {
      /* still clear session locally */
    } finally {
      emitAuthCleared()
    }
  },

  connect: (machineName: string) =>
    request<{ ok: boolean; log: string }>("/api/connect", {
      method: "POST",
      body: JSON.stringify({ machineName }),
    }),

  getClients: (machineName: string, filter: string = "all") =>
    request<{ clients: VpnClient[]; lastSyncedAt: string; log: string }>(
      `/api/clients?machineName=${encodeURIComponent(machineName)}&filter=${filter}`
    ),

  refreshClients: (machineName: string) =>
    request<{ clients: VpnClient[]; lastSyncedAt: string; log: string }>(
      "/api/clients/refresh",
      { method: "POST", body: JSON.stringify({ machineName }) }
    ),

  exportClientsDownload: async (machineName: string, filter: string) => {
    const url = `/api/clients/export?machineName=${encodeURIComponent(machineName)}&filter=${encodeURIComponent(filter)}`
    const res = await fetch(url, { credentials: "same-origin" })
    if (!res.ok) {
      if (res.status === 401) emitAuthCleared()
      const d = (await res.json().catch(() => ({}))) as { error?: string }
      throw new Error(d.error || "Erro ao exportar.")
    }
    const blob = await res.blob()
    const cd = res.headers.get("Content-Disposition")
    const match = cd?.match(/filename="([^"]+)"/)
    const fileName = match?.[1] ?? "export.csv"
    const objectUrl = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = objectUrl
    a.download = fileName
    a.click()
    URL.revokeObjectURL(objectUrl)
  },

  exportAllClientsWorkbookDownload: async (
    filter: string,
    machineNames: string[]
  ) => {
    const params = new URLSearchParams({ filter })
    for (const machineName of machineNames) {
      params.append("machineName", machineName)
    }
    const url = `/api/clients/export-all?${params.toString()}`
    const res = await fetch(url, { credentials: "same-origin" })
    if (!res.ok) {
      if (res.status === 401) emitAuthCleared()
      const d = (await res.json().catch(() => ({}))) as { error?: string }
      throw new Error(d.error || "Erro ao exportar.")
    }
    const blob = await res.blob()
    const cd = res.headers.get("Content-Disposition")
    const match = cd?.match(/filename="([^"]+)"/)
    const fileName = match?.[1] ?? "export.xlsx"
    const objectUrl = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = objectUrl
    a.download = fileName
    a.click()
    URL.revokeObjectURL(objectUrl)
  },

  getClientEvents: (machineName: string, clientName: string) =>
    request<{ events: VpnClientEvent[] }>(
      `/api/client-events?machineName=${encodeURIComponent(machineName)}&clientName=${encodeURIComponent(clientName)}`
    ),

  getClientConnections: (machineName: string, clientName: string) =>
    request<{ sessions: VpnConnectionSession[] }>(
      `/api/client-connections?machineName=${encodeURIComponent(machineName)}&clientName=${encodeURIComponent(clientName)}`
    ),

  getConnectedClients: (machineName: string) =>
    request<{ clients: ConnectedClient[]; log: string }>(
      `/api/connected-clients?machineName=${encodeURIComponent(machineName)}`
    ),

  createVpnUser: (data: {
    machineName: string
    clientName: string
    fullName: string
    email: string
    password?: string
    deliveryMethod: "download" | "email"
  }) =>
    request<{
      log: string
      deliveryMethod: string
      mailLog: string
      generatedPassword?: string
    }>("/api/users/create", { method: "POST", body: JSON.stringify(data) }),

  downloadVpnFile: async (machineName: string, clientName: string) => {
    const res = await fetch("/api/users/download-file", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ machineName, clientName }),
    })
    if (!res.ok) {
      if (res.status === 401) emitAuthCleared()
      const d = (await res.json()) as { error?: string }
      throw new Error(d.error || "Erro ao baixar arquivo.")
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${clientName}.ovpn`
    a.click()
    URL.revokeObjectURL(url)
  },

  sendVpnEmail: (
    machineName: string,
    clientName: string,
    password?: string
  ) =>
    request<{ log: string }>("/api/users/send-email", {
      method: "POST",
      body: JSON.stringify({ machineName, clientName, password }),
    }),

  updateVpnEmail: (machineName: string, clientName: string, email: string) =>
    request<{ log: string }>("/api/users/email", {
      method: "PUT",
      body: JSON.stringify({ machineName, clientName, email }),
    }),

  updateVpnProfile: (
    machineName: string,
    clientName: string,
    data: { fullName: string; email: string }
  ) =>
    request<{ log: string }>("/api/users/profile", {
      method: "PUT",
      body: JSON.stringify({
        machineName,
        clientName,
        fullName: data.fullName,
        email: data.email,
      }),
    }),

  revokeVpnUser: (machineName: string, clientName: string) =>
    request<{ log: string }>("/api/users/revoke", {
      method: "POST",
      body: JSON.stringify({ machineName, clientName }),
    }),

  deleteVpnUser: (machineName: string, clientName: string) =>
    request<{ log: string }>("/api/users/delete", {
      method: "POST",
      body: JSON.stringify({ machineName, clientName }),
    }),

  activateVpnUser: (data: {
    machineName: string
    clientName: string
    password?: string
    deliveryMethod: "download" | "email"
  }) =>
    request<{
      log: string
      deliveryMethod: string
      mailLog: string
      generatedPassword?: string
    }>("/api/users/activate", { method: "POST", body: JSON.stringify(data) }),

  getAppUsers: () => request<{ users: AppUser[] }>("/api/app-users"),

  createAppUser: (data: {
    username: string
    fullName: string
    email: string
    permissions: AppPermission[]
    password: string
  }) =>
    request<{ users: AppUser[]; log: string }>("/api/app-users", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateAppUser: (
    username: string,
    data: {
      fullName: string
      email: string
      permissions: AppPermission[]
      password?: string
    }
  ) =>
    request<{ users: AppUser[]; log: string }>(
      `/api/app-users/${encodeURIComponent(username)}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      }
    ),

  setAppUserStatus: (username: string, isActive: boolean) =>
    request<{ users: AppUser[]; log: string }>(
      `/api/app-users/${encodeURIComponent(username)}/status`,
      {
        method: "POST",
        body: JSON.stringify({ isActive }),
      }
    ),

  setAppUserPermissions: (username: string, permissions: AppPermission[]) =>
    request<{ users: AppUser[]; log: string }>(
      `/api/app-users/${encodeURIComponent(username)}/permissions`,
      {
        method: "POST",
        body: JSON.stringify({ permissions }),
      }
    ),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ log: string }>("/api/account/password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  getMailSettings: () =>
    request<{ mail: MailSettings }>("/api/mail-settings"),

  updateMailSettings: (payload: {
    from_name: string
    from_email: string
    resend_api_key?: string
  }) =>
    request<{ mail: MailSettings; log: string }>("/api/mail-settings", {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  getCertificates: () =>
    request<{ certificates: Certificate[] }>("/api/certificates"),

  createCertificate: (payload: {
    label: string
    /** PEM body to persist in SQLite (from file upload in the browser). */
    keyPem?: string
    /** Original file name for display when using `keyPem`. */
    fileName?: string
  }) =>
    request<{ certificates: Certificate[]; log: string }>("/api/certificates", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  deleteCertificate: (id: number) =>
    request<{ certificates: Certificate[]; log: string }>(
      `/api/certificates/${id}`,
      {
        method: "DELETE",
      }
    ),

  getDestinations: (includeInactive = false) =>
    request<{ destinations: Destination[] }>(
      includeInactive
        ? "/api/destinations?includeInactive=true"
        : "/api/destinations"
    ),

  setDestinationStatus: (machineName: string, isActive: boolean) =>
    request<{ destinations: Destination[]; log: string }>(
      `/api/destinations/${encodeURIComponent(machineName)}/status`,
      {
        method: "POST",
        body: JSON.stringify({ isActive }),
      }
    ),

  createDestination: (
    data: Omit<
      Destination,
      | "certificate_id"
      | "is_active"
    > & {
      certificate_id?: number | null
    }
  ) =>
    request<{ destinations: Destination[]; log: string }>("/api/destinations", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateDestination: (
    machineName: string,
    data: Partial<Destination>
  ) =>
    request<{ destinations: Destination[]; log: string }>(
      `/api/destinations/${encodeURIComponent(machineName)}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      }
    ),

  deleteDestination: (machineName: string) =>
    request<{ destinations: Destination[]; log: string }>(
      `/api/destinations/${encodeURIComponent(machineName)}`,
      {
        method: "DELETE",
      }
    ),
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B"
  const k = 1024
  const sizes = ["B", "KB", "MB", "GB", "TB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

export function formatDate(iso: string): string {
  if (!iso) return "-"
  const date = new Date(normalizeUtcDateInput(iso))
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

function normalizeUtcDateInput(value: string): string {
  const trimmed = value.trim()

  if (
    /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2})?$/.test(trimmed)
  ) {
    return `${trimmed.replace(" ", "T")}Z`
  }

  return trimmed
}
